import _ from '@/utils/lodash-optimized'
import moment from 'moment'
import { Component } from 'react'
import { connect } from 'react-redux'
import reminder from 'shared_src/reminder'

import http from '../../lib/http'
import { reasonable } from '../../lib/utils'
import {
  bindTargetMixin,
  CancelButton,
  CheckboxInput,
  DateInput,
  SaveButton,
  MaskedTextInput,
  TextInput,
  Validators
} from '../shared/FormBuilder'
import { CircularProgress } from '../shared/NarusMaterial'

const time =
  (msg = 'Please provide time in HH:mm format') =>
    v =>
      v && !moment(v, 'HH:mm').isValid() && msg

const OMIT = [
  'start',
  'end',
  'satisfaction',
  'billing',
  'billing_date',
  'billing_time',
  'offset',
  'symptoms_survey_reminder'
]

const prepareValue = settings =>
  _.merge(_.omit(settings, OMIT), {
    start: moment(settings.start).format('HH:mm'),
    end: moment(settings.end).format('HH:mm'),
    satisfaction: _.toString(settings.satisfaction),
    billing_date:
      settings.billing && moment(settings.billing).format('MM/DD/YYYY'),
    billing_time: settings.billing && moment(settings.billing).format('HH:mm'),
    symptoms_survey_reminder: _.toString(settings.symptoms_survey_reminder)
  })

const calculateBilling = value => {
  const { billing_date: billingDate, billing_time: billingTime } = value
  if (billingDate && billingTime) {
    const billing = moment(`${billingDate} ${billingTime}`, 'MM/DD/YYYY HH:mm')
    if (billing.isValid()) {
      return billing
    }
  }
  return null
}

const prepareSettings = params =>
  _.merge(_.omit(params, OMIT), {
    start: moment(params.start, 'HH:mm'),
    end: moment(params.end, 'HH:mm'),
    satisfaction: _.isEmpty(params.satisfaction) ? null : params.satisfaction,
    billing: calculateBilling(params),
    offset: moment().utcOffset(),
    symptoms_survey_reminder: _.isEmpty(params.symptoms_survey_reminder)
      ? null
      : params.symptoms_survey_reminder
  })

const loadSettings = () => dispatch => {
  dispatch({
    type: 'admin/settings/loading'
  })
  return http
    .getRequest('/admin/settings')
    .then(settings =>
      dispatch({
        type: 'admin/settings/loaded',
        settings: prepareValue(settings)
      }))
    .catch(error =>
      dispatch({
        type: 'admin/settings/error',
        error
      }))
}

const saveSettings = params => dispatch => {
  dispatch({
    type: 'admin/settings/loading'
  })
  return http
    .putRequest('/admin/settings', prepareSettings(params))
    .then(settings =>
      dispatch({
        type: 'admin/settings/loaded',
        settings: prepareValue(settings)
      }))
    .catch(error =>
      dispatch({
        type: 'admin/settings/error',
        error
      }))
}

const renderWarning = warning =>
  warning && (
    <div
      className='row'
      style={{ marginBottom: '10px', border: '2px solid orange', padding: '10px', borderRadius: '10px' }}
    >
      <div style={{ float: 'left' }}>
        <i className='fa fa-exclamation-triangle exclamation-yellow' />
      </div>

      <div style={{ marginLeft: '10px' }}>
        {warning}
      </div>
    </div>
  )

class SettingsForm extends bindTargetMixin(Component) {
  constructor (props) {
    super(props)
    this.state = {
      value: props.value
    }
  }

  renderSymptomsWarning () {
    const symptoms = _.parseInt(
        this.state?.value?.symptoms_survey_reminder
    )
    const warning =
      (symptoms > 0 &&
        `Remind mobile patients about symptoms survey after ${symptoms} minute(s) passed after the last symptom survey submit`) ||
      'Never remind mobile patients about symptoms survey'
    return renderWarning(warning)
  }

  renderSatisfactionWarning () {
      const satisfaction = this.state?.value?.satisfaction
    const warning = reminder.satisfaction.settings({
      satisfaction
    })
    return renderWarning(warning)
  }

  renderBillingWarning () {
      const billing = calculateBilling(this.state?.value)
    const warning = `${
      (!billing &&
        'Please specify billing date to limit how far back encounters can be edited. ') ||
      ''
    }Users can edit encounters signed up after ${
      (billing && moment(billing).format('MM/DD/YYYY HH:mm')) || 'that date'
    }`
    return renderWarning(warning)
  }

  renderEnrollmentEmailExpirationWarning () {
    const expiration = _.parseInt(
        this.state?.value?.enrollment_email_expiration
    )
    const warning =
      expiration > 0 &&
      `Enrollment email will be expired in ${expiration} minute(s)`
    return renderWarning(warning)
  }

  renderEnrollmentSMSExpirationWarning () {
    const expiration = _.parseInt(
        this.state?.value?.enrollment_sms_expiration
    )
    const warning =
      expiration > 0 &&
      `Enrollment SMS will be expired in ${expiration} minute(s)`
    return renderWarning(warning)
  }

  render () {
    const billingDate = calculateBilling({
        billing_date: this.state?.value?.billing_date,
      billing_time: '00:00'
    })
    return (
      <div style={{ padding: '10px' }}>
        <div className='section'>
          <CheckboxInput
            label='Sunday'
            name='sunday'
          />
          <CheckboxInput
            label='Monday'
            name='monday'
          />
          <CheckboxInput
            label='Tuesday'
            name='tuesday'
          />
          <CheckboxInput
            label='Wednesday'
            name='wednesday'
          />
          <CheckboxInput
            label='Thursday'
            name='thursday'
          />
          <CheckboxInput
            label='Friday'
            name='friday'
          />
          <CheckboxInput
            label='Saturday'
            name='saturday'
          />
        </div>
        <div className='section'>
          <h2>Working hours</h2>
          <MaskedTextInput
            mask='11:11'
            name='start'
            label='Start'
            required
            validators={[ Validators.required(), time() ]}
          />
          <MaskedTextInput
            mask='11:11'
            name='end'
            label='End'
            required
            validators={[ Validators.required(), time() ]}
          />
        </div>
        <div className='section' >
          <h2>Reminder periods in days</h2>
          <div className='row'>
            <TextInput
              label='Satisfaction survey'
              name='satisfaction'
              validators={[
                Validators.or(
                  Validators.empty(),
                  reasonable({
                    min: 0,
                    max: 365
                  })
                )
              ]}
            />
          </div>
          {this.renderSatisfactionWarning()}
        </div>
        <div className='section'>
          <h2>Symptoms Survey Reminder</h2>
          <div className='row'>
            <TextInput
              label='Reminder periods in minutes'
              name='symptoms_survey_reminder'
              validators={[
                Validators.or(
                  Validators.empty(),
                  reasonable({
                    min: 0,
                    max: 14400
                  })
                )
              ]}
            />
          </div>

          {this.renderSymptomsWarning()}
        </div>

        <div className='section'>
          <h2>Edit encounter</h2>
          <div className='row'>
            <div className='col-xs-6'>
              <DateInput
                name='billing_date'
                label='Billing date'
              />
            </div>
            {billingDate && (
              <div className='col-xs-3'>
                <MaskedTextInput
                  mask='11:11'
                  name='billing_time'
                  label='Billing time'
                  required
                  validators={[ Validators.required(), time() ]}
                />
              </div>
            )}

          </div>
          {this.renderBillingWarning()}
        </div>

        <div className='section'>

          <h2>Lifetime of enrollment Email</h2>

          <div className='row'>
            <TextInput
              label='Lifetime in minutes'
              name='enrollment_email_expiration'
              validators={[
                Validators.required(),
                reasonable({
                  min: 180,
                  max: 10080
                })
              ]}
            />
          </div>

          {this.renderEnrollmentEmailExpirationWarning()}
        </div>

        <div className='section'>

          <h2>Lifetime of enrollment SMS</h2>

          <div className='row'>
            <TextInput
              label='Lifetime in minutes'
              name='enrollment_sms_expiration'
              validators={[
                Validators.required(),
                reasonable({
                  min: 180,
                  max: 10080
                })
              ]}
            />
          </div>

          {this.renderEnrollmentSMSExpirationWarning()}
        </div>

        <div className='bottom-buttons'>
          <CancelButton
            formKey={this.props.formKey}
            onClick={this.props.onCancel}
          />

          <SaveButton
            formKey={this.props.formKey}
            onClick={() => this.props.onSave(this.state.value)}
          />
        </div>
      </div>
    )
  }
}

class Settings extends Component {
  UNSAFE_componentWillMount () {
    const { dispatch } = this.props
    dispatch(loadSettings())
  }

  render () {
    const { settings: propSettings, dispatch } = this.props
    return (
      <div className='main'>
        <h1><b>Settings</b></h1>
        <div className='row'>
          {propSettings.loading
            ? (
              <CircularProgress />
            )
            : (
              <SettingsForm
                formKey='settings'
                value={propSettings.settings}
                onSave={settings => dispatch(saveSettings(settings))}
                onCancel={() => dispatch(loadSettings())}
              />
            )}
        </div>
      </div>
    )
  }
}
export default connect(redux => ({
    settings: redux?.admin?.settings ?? {}
}))(Settings)
